import Axios from "axios";
import { getBaseAuthUrl, getBaseApiUrl } from "../constants/base-urls";
import { ApiAddress, UserProfileData } from "../contexts/card-holder";
import { cleanBase64 } from "../utils";
import * as Cache from "../services/cache";
import { CardHolderData } from "../contexts/startup/types";
import { StandTemplate } from "../interfaces/standTemplate.interface";
import config from '../assets/app.config.json';
import { AppConfig } from "../contexts/startup/types";

const appConfig: AppConfig = config;

Axios.defaults.headers["Content-Type"] = "application/json";

Cache.get("appVersion").then((data) => {
    Axios.defaults.headers.common["Stand-App-Version"] = data;
});

Axios.defaults.baseURL = getBaseApiUrl();

export async function setBaseURL(baseURL: string) {
    if (baseURL) {
        Axios.defaults.baseURL = baseURL;
    }
}

export function setAuthorizationHeader(accessToken: string | null) {
    if (accessToken) {
        Axios.defaults.headers.Authorization = `Bearer ${accessToken}`;
    } else {
        Axios.defaults.headers.Authorization = null;
    }
}

export async function getStartupData() {
    return Axios.get(`/api/v2/stand/startup`);
}

export async function updatePersonLanguage(
    langCode: string,
    personId?: string
) {
    return Axios.put(`/api/v2/stand/language/${personId}`, langCode);
}

export async function updateDefaultProfile(data: {}) {
    return Axios.post(`/api/v2/stand/savedefaultprofile`, data);
}

export async function updateProfilePreference(data: {}) {
    return Axios.put(`/api/v2/stand/profilePreference`, data);
}

export async function getCardHolderInfo() {
    return Axios.get<UserProfileData>(`/api/v2/stand/user`);
}

export async function getNewCardHolderInfo(profileId: string) {
    return Axios.get(`/api/v2/stand/user?switchProfile=${profileId}`);
}

export async function updateUser(data: Partial<UserProfileData>) {
    return Axios.put(`/api/v2/stand/user`, data);
}

export async function updateCardHolder(cardholderid: string, data: any) {
    return Axios.put(`/api/v2/stand/${cardholderid}`, data);
}

export async function getEnumOptions() {
    return Axios.get(`/api/v2/stand/enumOptions`);
}

export async function getWizardSettings() {
    return Axios.get(`/api/v2/stand/GeneralStandSettings/wizard`);
}

export async function getDetailsField() {
    return Axios.get(`/api/v2/stand/GeneralStandSettings/detailsField`);
}

export async function getAllThemes() {
    return Axios.get(`/api/v2/stand/GeneralStandSettings`);
}

export async function getSystemLabels() {
    return Axios.get(`/api/v2/admincenter/setup/controllercms/systemlabels`);
}

export type UpdatePhotoParams = {
    photo: string;
    orientation?: string | number;
    approved?: boolean;
    personId?:string;
};

export async function updatePhoto({
    photo,
    orientation = "0",
    approved = false,
    personId,
}: UpdatePhotoParams) {
    return Axios.post(`/api/v2/stand/photos/update`, {
        content: cleanBase64(photo),
        orientation,
        approved,
    });
}

export type UpdateCardPayload = {
    status: CardHolderData["cardStatus"];
};

export type CrateCardPayload = {
    cardholder: CardHolderData["cardholderId"];
};

export async function createCard(payload: CrateCardPayload) {
    return Axios.post(`/api/v2/stand/cards`, payload);
}

export async function changePassword(
    currentPassword: string,
    newPassword: string
) {
    return Axios.post(getBaseAuthUrl() + `/api/stand/changepassword`,{
        currentPassword,
        newPassword,
    });
}

export async function uploadIdentity({ file }: { file: string },personId?: string) {
    return Axios.post(`/api/v2/stand/documents/${personId}/add`, {
        content: cleanBase64(file),
    });
}

export async function uploadSignature({ file }: { file: string }, personId?: string) {
    return Axios.post(`/api/v2/stand/signatures/${personId}/add`, {
        content: cleanBase64(file),
    });
}

// tslint:disable-next-line:no-shadowed-variable
export async function pushToken(pushToken: string) {
    return Axios.post(`/api/v2/stand/userdevicepushtokens`, {
        pushToken,
    });
}

// tslint:disable-next-line:no-shadowed-variable
export async function deletePushToken(pushToken: string) {
    return Axios.delete(`/api/v2/stand/userdevicepushtokens/${pushToken}`);
}

export async function getNotifications() {
    return Axios.get("/api/v2/notifications?orderby=StatusRead&ascending=true");
}

export async function getNewCredential() {
    return Axios.get("api/v2/notifications/newCredential");
}

export async function getCardCredentials(cardHolderId: string) {
    return Axios.post(`/api/v2/stand/cards/credentials`, {
        cardholderIds: [cardHolderId],
    });
}

export async function markAsRead(id: string) {
    return Axios.post(`/api/v2/notifications/${id}/markAsRead`);
}

export async function deleteNotification(id: string) {
    return Axios.delete(`/api/v2/notifications/${id}`);
}

export async function getUserProfiles() {
    return Axios.get(`/api/v2/stand/cardholders`);
}

export type AddAddressPayload = Omit<
    ApiAddress,
    "created" | "id" | "object" | "type"
>;

export async function addAddress(payload: AddAddressPayload) {
    return Axios.post(`/api/v2/stand/user/address`, payload);
}

export type UpdateAddressPayload = ApiAddress;

export async function updateAddress(payload: UpdateAddressPayload) {
    return Axios.put(`/api/v2/stand/user/address`, payload);
}

export async function updateCardStatus(
    cardId: string,
    status: string,
    typeId: string,
    profileId: string,
    startDate: Date,
    endDate: Date
) {
    return Axios({
        method: "post",
        url: `/api/v2/stand/cards/updatePerType`,
        data: {
            cardId: cardId,
            status: status,
            type: typeId,
            profileId: profileId,
            startDate: startDate,
            endDate: endDate,
        },
        validateStatus: function (status) {
            return status <= 500;
        },
    });
}

export async function getTemplateByProfileId(profileId?: string): Promise<StandTemplate> {
    var response = await Axios.get<StandTemplate>(`/api/v2/admincenter/setup/stand/standTemplates/${profileId}`);
    return response.data;
}